"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../../../navigation/Layout/Layout";

import PharmacyHeader from "../../components/PharmacyHeader/PharmacyHeader";
import PharmacyList from "../../components/PharmacyList/PharmacyList";

import usePlaces from "../../hooks/usePlaces";
import { useTour } from "../../../appointments/hooks/useTour";
import { infTourSteps } from "./infTourConfig";

import "./Inf.css";

export default function Inf() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [shouldStartTour, setShouldStartTour] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(20); // ⬅️ Show first 20

  const listRef = useRef(null);

  // ---- store places (nurses)
  const {
    items,
    loading,
    error,
    loadCategory,
    selectPlace,
  } = usePlaces();

  // Guided tour
  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "inf";

    if (hasGuide || hasTour) {
      setTimeout(() => setShouldStartTour(true), 1000);
    }
  }, [searchParams]);

  useTour(shouldStartTour, infTourSteps);

  // Load nurses
  useEffect(() => {
    loadCategory("nurses", { limit: 200, skip: 0 });
  }, [loadCategory]);

  // Filter + pagination
  const filtered = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const q = searchQuery.trim().toLowerCase();

    if (!q) return list.slice(0, visibleCount);

    return list.filter(
      (n) =>
        (n.name || "").toLowerCase().includes(q) ||
        (n.address || "").toLowerCase().includes(q) ||
        (n.vicinity || "").toLowerCase().includes(q)
    );
  }, [items, searchQuery, visibleCount]);

  // Infinite scroll
  useEffect(() => {
    const div = listRef.current;
    if (!div) return;

    const onScroll = () => {
      if (loading) return;
      if (div.scrollTop + div.clientHeight >= div.scrollHeight - 40) {
        setVisibleCount((prev) => prev + 20);
      }
    };

    div.addEventListener("scroll", onScroll);
    return () => div.removeEventListener("scroll", onScroll);
  }, [loading]);

  // Reset pagination when search changes
  useEffect(() => {
    if (searchQuery.trim() !== "") {
      setVisibleCount(200);
    } else {
      setVisibleCount(20);
    }
  }, [searchQuery]);

	  return (
    <AppLayout>
      <main className="inf-main inf--listOnly">
	        <div className="inf-container">
	          <PharmacyHeader
	            title={t("locators.nurses.title")}
	            subtitle={t("locators.nurses.subtitle")}
            enableFilters={false}
            showTabs={false}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            activeTab="Nurse"
            setActiveTab={() => {}}
          />

          <section className="inf-list-card">
	            <div className="inf-list-scroll" ref={listRef}>
	              {loading && (
	                <p className="inf-empty">{t("locators.nurses.loading")}</p>
	              )}
	              {!loading && error && (
	                <p className="inf-empty">
	                  {t("locators.nurses.failed", { error: String(error) })}
	                </p>
	              )}
	              {!loading && !error && filtered.length === 0 ? (
	                <p className="inf-empty">{t("locators.nurses.empty")}</p>
	              ) : (
                <PharmacyList
                  pharmacies={filtered}
                  onPharmacyClick={(n) => selectPlace(n)}
                  selectedPharmacy={null}
                />
              )}
            </div>
          </section>
        </div>
      </main>
    </AppLayout>
  );
}
